import React, { useState } from 'react';
import { Box, Typography, Dialog, DialogContent, DialogTitle } from '@mui/material';
import EnableAccount from './EnableAccount';
import { formatDate } from './generalUtils';
import { getErrorMessage } from './components/ErrorCatcher';
import { useNavigate } from 'react-router-dom';

const OnPlanEndErrorHandler = ({ 
  error, 
  errorCode = null, 
  freeTrialExpirationDate = null, 
  onEnableAccountSuccess: parentOnEnableAccountSuccess, 
  showDialog = false,
  navigateToLogin = false,
  resetError = null,
  }) => {
  const [showDialogState, setShowDialogState] = useState(showDialog);
  const [accountEnabled, setAccountEnabled] = useState(false);
  
  const onEnableAccountSuccess = (renewalDate) => {
    setAccountEnabled(true);
    if (resetError) {
      resetError();
    }
    parentOnEnableAccountSuccess?.(renewalDate);
  };

  const navigate = useNavigate();

  
  const handleBlurAndReset = () => {

    if (!showDialog ) {
      if (accountEnabled){ 
        resetError?.();
      }
        return;    
      }

    try {
      if (document?.activeElement?.blur) {
        document.activeElement.blur();
      }
    } catch (e) {
      // best-effort
    }
    try {
      resetError?.();
    } catch (e) {
      console.warn('resetError failed', e);
    }
    setShowDialogState(false);
  };

  const onClose = () => {
    console.log('OnPlanEndErrorHandler: dialog closed', resetError, navigateToLogin);
    handleBlurAndReset();
    if (navigateToLogin) {
      navigate('/main');
    }
  };

  const renderContent = () => {
    if (!error) return null;

    const { msg, code } = getErrorMessage(error);
    const finalErrorCode = errorCode || code;

    const makeEnableAccount = (introMessage) => (
      <EnableAccount
        showTrial={false}
        introMessage={introMessage}
        setShowParent={handleBlurAndReset}
        onEnableAccountSuccess={onEnableAccountSuccess}
      />
    );

    if (finalErrorCode === 4032) {
      const reasonMessage = () => {
        const expDate = error?.response?.data?.details?.trialExpirationDate;
        return expDate ? (
          <Typography>
            Your trial expired on {formatDate(expDate)}. To continue learning, please, make a payment
          </Typography>
        ) : null;
      };

      return (
        <Box>
          {freeTrialExpirationDate ? (
            <Typography>
              You successfully started the free trial which expires on {freeTrialExpirationDate}
            </Typography>
          ) : (
            makeEnableAccount(reasonMessage)
          )}
        </Box>
      );
    }

    if (finalErrorCode === 4033) {
      const reasonPaymentRenewal = () => {
        const expDate = error?.response?.data?.details?.nextRenewalDate;
        return expDate ? (
          <Typography>
            Your monthly membership expired on {formatDate(expDate)}. To continue learning, please, make a payment.
          </Typography>
        ) : null;
      };
      return makeEnableAccount(reasonPaymentRenewal);
    }

    if (finalErrorCode === 4100) {
      const reasonCommandLimit = () => (
        <Typography>{msg} To continue learning, please, make a payment</Typography>
      );
      return makeEnableAccount(reasonCommandLimit);
    }

    return null;
  };

  if (showDialog) {
    return (
      <Dialog
        open={showDialogState}
        onClose={(event, reason) => {
          // ignore backdrop/escape automated closes so we control resetError timing
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          onClose();
        }}
        maxHeight="100dvh"
        maxWidth="100%"
        sx={{
          backgroundColor: 'rgba(255, 245, 238, 0.5)', // 50% transparent seashell color
          borderRadius: 2,
          p: 2,
        }}
      >
        <DialogTitle>Expiration Notice</DialogTitle>
        <DialogContent>{renderContent()}</DialogContent>
      </Dialog>
    );
  }

  return renderContent();
};

export default OnPlanEndErrorHandler;
